const moneyFormats = new Map<string, Intl.NumberFormat>();

/** Currency amount. Unit costs pass `precise` to keep up to 4 decimals (e.g. €0.2833). */
export function money(value: string | number | null | undefined, currency = 'EUR', precise = false) {
  if (value === null || value === undefined || value === '') return '—';
  const key = `${currency}|${precise}`;
  let fmt = moneyFormats.get(key);
  if (!fmt) {
    const digits = { minimumFractionDigits: 2, maximumFractionDigits: precise ? 4 : 2 };
    try {
      fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency, ...digits });
    } catch {
      // A half-typed currency code is not a currency yet; show the number on its own.
      return new Intl.NumberFormat(undefined, digits).format(Number(value));
    }
    moneyFormats.set(key, fmt);
  }
  return fmt.format(Number(value));
}

const qtyFormat = new Intl.NumberFormat();
export const qty = (value: number | string | null | undefined) => (value === null || value === undefined ? '—' : qtyFormat.format(Number(value)));

export function date(value: string | null | undefined) {
  if (!value) return '—';
  // Plain dates are calendar days already; timestamps are shown in the property's time zone.
  if (value.length === 10) return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone });
}

// Times are shown in the selected property's time zone (set by the session), not the device's.
let timeZone: string | undefined;
export function setDisplayTimeZone(zone: string | undefined) {
  timeZone = zone;
}

export function dateTime(value: string | null | undefined) {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone });
}

/** Calendar date (YYYY-MM-DD) at the property, n days ago. */
export function daysAgo(n: number) {
  const d = new Date(Date.now() - n * 86400000);
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

/** Today's date at the property, for date inputs and defaults. */
export const today = () => daysAgo(0);

export const label = (value: string | null | undefined) => (value ? (value.charAt(0).toUpperCase() + value.slice(1)).replace(/_/g, ' ') : '—');

export const MOVEMENT_LABELS: Record<string, string> = {
  receipt: 'Stock in', issue: 'Issue', waste: 'Waste', transfer_out: 'Transfer out', transfer_in: 'Transfer in',
  adjustment: 'Adjustment', count: 'Count', cost_adjustment: 'Cost adjustment', reversal: 'Reversal', revaluation: 'Revaluation',
};

export const REASON_LABELS: Record<string, string> = {
  damaged: 'Damaged', expired: 'Expired', stained_torn: 'Stained or torn', lost: 'Lost', guest_took: 'Guest took', theft: 'Theft',
  count_variance: 'Count variance', correction: 'Correction', other: 'Other',
};

export const METHOD_LABELS = { FIFO: 'FIFO', WAVG: 'Weighted average' } as const;

/** Pill colours from the design tokens (see .pill-* in styles.css). */
export type Tone = 'success' | 'warning' | 'error' | 'info' | 'neutral';

/**
 * PRD movement types: the ledger keeps its own types (transfer_out/in, count), but people see one of In, Out,
 * Transfer, Waste or Adjustment. Corrections to cost or value keep their own name.
 */
export const MOVEMENT_KINDS: Record<string, string> = {
  receipt: 'In', issue: 'Out', waste: 'Waste', transfer_out: 'Transfer', transfer_in: 'Transfer', adjustment: 'Adjustment', count: 'Adjustment',
  cost_adjustment: 'Cost adjustment', reversal: 'Reversal', revaluation: 'Revaluation',
};
const KIND_TONES: Record<string, Tone> = { In: 'success', Out: 'info', Transfer: 'neutral', Waste: 'error', Adjustment: 'warning' };

/** PRD movement label and pill tone for a ledger type (unknown types show as they are). */
export function movementKind(type: string): { label: string; tone: Tone } {
  const text = MOVEMENT_KINDS[type] ?? label(type);
  return { label: text, tone: KIND_TONES[text] ?? 'neutral' };
}

/** Waste reasons in the order the stock out form lists them (PRD flow 4). */
export const WASTE_REASONS = ['damaged', 'expired', 'stained_torn', 'lost', 'guest_took', 'other'] as const;

/** Purchase request statuses as the PRD names them. */
export const PR_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', attachment_generated: 'Attachment generated', signed: 'Signed', filed: 'Filed',
  cancelled: 'Cancelled', partially_received: 'Partially received', received: 'Received',
};

/**
 * One word per purchase order (PRD flow 9): Pending (not placed yet), Overdue (past its expected date), Due (expected
 * today), Placed, then Received / Closed / Cancelled. Part-received orders read by date like placed ones.
 */
export function poDisplayStatus(order: { status: string; expected_date: string | null }, todayIso: string): { label: string; tone: Tone } {
  switch (order.status) {
    case 'draft': return { label: 'Pending', tone: 'warning' };
    case 'received': return { label: 'Received', tone: 'success' };
    case 'closed': return { label: 'Closed', tone: 'neutral' };
    case 'cancelled': return { label: 'Cancelled', tone: 'error' };
  }
  if (order.expected_date && order.expected_date < todayIso) return { label: 'Overdue', tone: 'error' };
  if (order.expected_date === todayIso) return { label: 'Due', tone: 'info' };
  return { label: 'Placed', tone: 'neutral' };
}
